import {
  humanId,
  numberMoney,
  stripHtml,
  adjustmentType,
  dateISOToDateShortReporting,
  percentage,
  phoneNumberUS,
} from 'Data/format'

export function toField(fields, column) {
  return fields[column.column_name]
}

export function mapField(field) {
  switch (field.type) {
    case 'GROUP': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        groupId: field.id,
        headerName: field.text,
        children: field.children.map(children => mapField(children)),
        context: field.context,
      }
    }
    case 'DATE_TIME': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        cellDataType: 'dateTimeString',
        filter: 'agDateColumnFilter',
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        valueFormatter: { type: 'datetime' },
        headerTooltip: field.headerTooltip,
        filterParams: {
          comparator: (filterDate, cellValue) => {
            let dateTimeParts = cellValue.split('T')
            let dateParts = dateTimeParts[0].split('-')
            let cellDate = new Date(
              dateParts[0],
              dateParts[1] - 1,
              dateParts[2]
            )
            if (cellDate < filterDate) {
              return -1
            } else if (cellDate > filterDate) {
              return 1
            }
            return 0
          },
        },
        context: field.context,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
      }
    }
    case 'DATE': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        cellDataType: 'dateString',
        filter: 'agDateColumnFilter',
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        valueFormatter: { type: 'date' },
        headerTooltip: field.headerTooltip,
        filterParams: {
          comparator: (filterDate, cellValue) => {
            let dateTimeParts = cellValue.split('T')
            let dateParts = dateTimeParts[0].split('-')

            // The filter data input doesn't handle time leaving it out in the meantime
            // let timeParts = dateTimeParts[1].split('Z')[0].split(':')

            let cellDate = new Date(
              dateParts[0],
              dateParts[1] - 1,
              dateParts[2]
              // timeParts[0],
              // timeParts[1],
              // timeParts[2]
            )

            if (cellDate < filterDate) {
              return -1
            } else if (cellDate > filterDate) {
              return 1
            }
            return 0
          },
        },
        context: field.context,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
      }
    }

    case 'MONEY': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        minWidth: 150,
        enableValue: true,
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        aggFunc: field.aggFunc ?? 'sum',
        valueFormatter: { type: 'currency', symbol: '$' },
        context: field.context,
        headerTooltip: field.headerTooltip,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
        cellStyle: function (params) {
          if (params.value < 0) {
            //Here you can check the value and based on that you can change the color
            //mark police cells as red
            return { color: '#D64545' }
          } else {
            return null
          }
        },
      }
    }

    case 'NUMBER': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        enableValue: true,
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        aggFunc: field.aggFunc ?? 'sum',
        valueFormatter: { type: 'number' },
        context: field.context,
        headerTooltip: field.headerTooltip,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
        cellStyle: function (params) {
          if (params.value < 0) {
            return { color: '#D64545' }
          } else {
            return null
          }
        },
      }
    }

    case 'PERCENTAGE': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        enableValue: true,
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        aggFunc: field.aggFunc ?? 'avg',
        valueFormatter: { type: 'percentage' },
        context: field.context,
        headerTooltip: field.headerTooltip,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
        cellStyle: function (params) {
          if (params.value < 0) {
            return { color: '#D64545' }
          } else {
            return null
          }
        },
      }
    }

    case 'HUMAN_ID': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        valueFormatter: { type: 'humanid' },
        context: field.context,
        headerTooltip: field.headerTooltip,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
      }
    }

    case 'ADJUSTMENT_TYPE': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        valueFormatter: { type: 'adjustment_type' },
        context: field.context,
        headerTooltip: field.headerTooltip,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
      }
    }

    case 'CUSTOM':
    case 'PATIENT_LINK': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        context: field.context,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
        cellRenderer: field.cellRenderer,
      }
    }

    case 'STRING': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        context: field.context,
        headerTooltip: field.headerTooltip,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
      }
    }

    case 'DUE_DATE': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        filter: 'agTextColumnFilter',
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        context: field.context,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
      }
    }

    case 'PHONE': {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        valueFormatter: { type: 'phone' },
        context: field.context,
        headerTooltip: field.headerTooltip,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
      }
    }

    case 'TEXT':
    default: {
      return {
        field: field.id,
        colId: field.id,
        headerCheckboxSelection: field.headerCheckboxSelection,
        checkboxSelection: field.checkboxSelection,
        valueGetter: field.valueGetter,
        headerName: field.text,
        filter: 'agTextColumnFilter',
        enableRowGroup: true,
        valueFormatter: { type: 'text' },
        wrapHeaderText: true, // Wrap Text
        autoHeaderHeight: true, // Adjust Cell Height to Fit Wrapped Text
        context: field.context,
        headerTooltip: field.headerTooltip,
        rowGroup: !!field.rowGroup,
        hide: !!field.hide,
      }
    }
  }
}

export let VALUE_FORMATTERS = {
  currency: params =>
    params.value
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(params.value)
      : '$ 0.00',
  number: params => params.value ?? 0,
  percentage: params => {
    if (!params.value) {
      return '0%'
    }

    let value =
      typeof params.value === 'string'
        ? parseFloat(params.value)
        : typeof params.value === 'object'
          ? (params.value.toNumber() ?? params.value.value)
          : params.value

    return percentage(value)
  },
  datetime: params =>
    params.value ? dateISOToDateShortReporting(params.value) : '',
  date: params =>
    params.value ? dateISOToDateShortReporting(params.value) : '',
  humanid: params => (params.value ? humanId(params.value) : ''),
  adjustment_type: params => (params.value ? adjustmentType(params.value) : ''),
  text: params => (params.value ? stripHtml(params.value) : ''),
  phone: params =>
    params.value ? params.value.split(',').map(phoneNumberUS).join(', ') : '',
}

export let GENERATE_REPORT_COLUMNS = {
  financial: function generateFinanceReportColumns(fields, columns) {
    let result = []
    let yearGroups = {}

    columns.forEach(({ column_name }) => {
      let [year, category, type] = column_name.split('_')

      if (column_name === 'MONTH') {
        let monthTemplate = fields['MONTH']
        if (monthTemplate) result.push(monthTemplate)
      } else if (year && category && type) {
        if (!yearGroups[year]) {
          yearGroups[year] = {
            id: year,
            type: 'GROUP',
            text: year,
            children: [],
          }
        }

        let group = yearGroups[year].children.find(
          g => g.id === `${year}_${type}`
        )

        if (!group) {
          yearGroups[year].children.push({
            id: `${year}_${type}`,
            type: 'GROUP',
            text: type === 'PRODUCTION' ? 'Production' : 'Collection',
            children: [],
          })
        }

        let subGroup = yearGroups[year].children.find(
          g => g.id === `${year}_${type}`
        )

        let column = {
          id: column_name,
          type: 'MONEY',
          text:
            category === 'NET'
              ? 'Net'
              : category === 'GROSS'
                ? 'Gross'
                : 'Adj.',
        }

        if (category === 'NET') {
          column.context = {
            hide: false,
          }
        }

        subGroup.children.push(column)
      }
    })

    return result.concat(
      Object.keys(yearGroups)
        .sort((a, b) => b.localeCompare(a))
        .map(key => yearGroups[key])
    )
  },
  collections: function generateCollectionReportColumns(
    fields,
    columns,
    tableData
  ) {
    let columnNames = new Set(columns.map(column => column.column_name))
    let dynamicFieldNames = new Set()

    for (let item of tableData) {
      for (let key of Object.keys(item)) {
        if (!columnNames.has(key)) {
          dynamicFieldNames.add(key)
        }
      }
    }

    let dynamicFields = Array.from(dynamicFieldNames)
      .sort((a, b) => a.localeCompare(b))
      .map(fieldName => ({
        id: fieldName,
        type: 'MONEY',
        text: fieldName,
      }))

    let existingFields = columns.map(column => toField(fields, column))

    return existingFields.concat(dynamicFields)
  },
  cash_flow: function generateCashFlowReportColumns(fields, columns) {
    let new_columns = [
      {
        id: 'year_due_date',
        type: 'DUE_DATE',
        text: 'Year',
        hide: true,
        valueGetter: params =>
          params.data?.DUE_DATE
            ? new Date(params.data.DUE_DATE).getFullYear()
            : undefined,
      },
      {
        id: 'month_due_date',
        type: 'DUE_DATE',
        text: 'Month',
        hide: true,
        valueGetter: params => {
          if (!params.data?.DUE_DATE) return undefined
          return new Date(params.data.DUE_DATE).toLocaleString('en-US', {
            month: 'long',
          })
        },
      },
    ]

    let [location, due_date, ...rest] = columns.map(column =>
      toField(fields, column)
    )

    return [location, ...new_columns, due_date, ...rest].filter(Boolean)
  },
  statements: (fields, columns) => {
    return Object.values(fields)
  },
}

let MONTHS_MAP = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
}

export let CHART_CONFIGURATION_HANDLER = {
  financial: (_, data) => {
    return {
      title: 'Financial',
      series: [
        {
          type: 'line',
          xKey: 'MONTH',
          yKey: 'COLLECTION',
          yName: 'Collection',
        },
        {
          type: 'line',
          xKey: 'MONTH',
          yKey: 'PRODUCTION',
          yName: 'Production',
        },
      ],
      axes: [
        {
          type: 'category',
          position: 'bottom',
          title: {
            text: 'Month',
          },
          label: {
            fontFamily: 'Inter',
            fontSize: 12,
            formatter: params => {
              let [year, month] = params.value.split('_')
              return `${year} ${MONTHS_MAP[month]}`
            },
          },
        },
        {
          type: 'number',
          position: 'left',
          title: {
            text: 'Net Amount ($)',
          },
          label: {
            fontFamily: 'Inter',
            fontSize: 12,
            formatter: params => {
              return numberMoney(params.value)
            },
          },
        },
      ],
      chartData: generateFinancialChartData(data),
    }
  },
}

function generateFinancialChartData(data) {
  let result = data
    .map(item => {
      return Object.keys(item)
        .filter(
          key =>
            key.endsWith('NET_PRODUCTION') || key.endsWith('NET_COLLECTION')
        )
        .reduce((acc, key) => {
          let currentYear = key.split('_')[0]
          let new_key = key.endsWith('NET_PRODUCTION')
            ? 'PRODUCTION'
            : 'COLLECTION'

          if (!acc[`${currentYear}_${item.MONTH_NUMBER}`]) {
            acc[`${currentYear}_${item.MONTH_NUMBER}`] = {}
          }

          acc[`${currentYear}_${item.MONTH_NUMBER}`][new_key] = item[key]

          return acc
        }, {})
    })
    .flatMap(item =>
      Object.keys(item).map(key => ({
        MONTH: key,
        ...item[key],
      }))
    )
    .sort((a, b) => a.MONTH.localeCompare(b.MONTH))
  return result
}
